import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_2 = { class: "d-flex gap-4 align-items-center mb-4" }
const _hoisted_3 = ["id", "onChange", "disabled"]
const _hoisted_4 = { class: "dialog-footer d-flex justify-content-between align-items-center" }

import { ref, nextTick } from "vue";
import DocsServices from "../../services/DocsServices";
import { ElMessage } from "element-plus";
import { useStore } from "@/store";
import { Delete, Upload } from "@element-plus/icons-vue";
import { LanguageTypes } from "@/core/types/LanguageTypes";


export default /*#__PURE__*/_defineComponent({
  __name: 'UploadDocuments',
  emits: ["submitted"],
  setup(__props, { expose: __expose, emit: emits }) {

const store = useStore();
const user = store.state.AuthModule.user;
const title = ref("");
const dialogRef = ref(false);
const isLoading = ref(false);
const id = ref(0);
const fileList = ref<any>({});
const uploadedLinks = ref<any>([]);
const operatorInfo = ref<any>({
  name: user.nativeName,
  email: user.email,
  updatedAt: new Date().toISOString(),
});
const docsLanguages = ref<any>({});

const uploadRefs = ref<any>({});

for (const [language, file] of Object.entries(fileList.value)) {
  uploadRefs.value[language] = null;
}
const bulkInputRef = ref<any>(null);
const unmatchedFiles = ref<string[]>([]);


const handleFileUpload = (event: Event, key: any) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    const files = Array.from(target.files);
    files.forEach((file) => {
      const markedFile = Object.assign(file, {
        language: key,
      });
      fileList.value[key] = markedFile;
    });
  }
};

// 根据文件名末尾的语言标识匹配到对应的语言 code（一个标识可对应多个 code）
const aliasToCodes: Record<string, string[]> = (() => {
  const lookup: Record<string, string[]> = {};
  LanguageTypes.all.forEach((lang) => {
    lookup[lang.code.toLowerCase()] = [lang.code];
    lookup[lang.name.toLowerCase()] = [lang.code];
    if (lang.englishName) lookup[lang.englishName.toLowerCase()] = [lang.code];
  });
  // 额外的俗称/别名
  const extra: Record<string, string[]> = {
    cambodian: ["km-kh"],
    chinese: ["zh-cn"],
    mandarin: ["zh-cn"],
    "simplified chinese": ["zh-cn"],
    simchinese: ["zh-cn"],
    // 繁体中文默认同时匹配 zh-tw 和 zh-hk
    "zh-tw": ["zh-tw"],
    "zh-hk": ["zh-hk"],
    "traditional chinese": ["zh-tw", "zh-hk"],
    trachinese: ["zh-tw", "zh-hk"],
  };
  Object.entries(extra).forEach(([k, v]) => (lookup[k] = v));
  return lookup;
})();

// 别名按长度降序，优先匹配更长的（如 simplified/traditional chinese 优先于 chinese）
const sortedAliases = Object.keys(aliasToCodes).sort(
  (a, b) => b.length - a.length
);

const matchLanguageCodes = (fileName: string): string[] => {
  // 去掉扩展名，从文件名结尾匹配语言标识（兼容 " - " 或仅空格分隔）
  const baseName = fileName
    .replace(/\.[^.]+$/, "")
    .trim()
    .toLowerCase();
  for (const alias of sortedAliases) {
    if (!baseName.endsWith(alias)) continue;
    const before = baseName.length - alias.length;
    // 必须是开头，或前面是空格/连字符，避免误匹配单词内部
    if (before === 0 || /[\s-]/.test(baseName[before - 1])) {
      return aliasToCodes[alias];
    }
  }
  return [];
};

const handleBulkUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  const files = Array.from(target.files);
  unmatchedFiles.value = [];
  files.forEach((file) => {
    // 仅接受当前文档支持的语言
    const codes = matchLanguageCodes(file.name).filter((code) =>
      Object.prototype.hasOwnProperty.call(docsLanguages.value, code)
    );
    if (codes.length) {
      codes.forEach((code) => {
        const markedFile = Object.assign(file, { language: code });
        fileList.value[code] = markedFile;
      });
    } else {
      unmatchedFiles.value.push(file.name);
    }
  });
  // 把匹配到的文件写回对应语言的 input，让文件名像手动选择一样显示
  nextTick(() => {
    Object.entries(fileList.value).forEach(([code, f]) => {
      const inputEl = uploadRefs.value[code];
      if (inputEl) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(f as File);
        inputEl.files = dataTransfer.files;
      }
    });
  });
  if (unmatchedFiles.value.length) {
    ElMessage.warning(
      `${unmatchedFiles.value.length} file(s) could not be matched to a language`
    );
  }
  // 清空 input，方便再次选择相同文件
  target.value = "";
};

const triggerBulkUpload = () => {
  bulkInputRef.value?.click();
};

const show = (_data: any, _languages) => {
  id.value = _data.id;
  title.value = _data.title;
  docsLanguages.value = { ..._languages };
  // 确保繁体中文 zh-tw 也能上传（API 列表里可能没有）
  if (!Object.prototype.hasOwnProperty.call(docsLanguages.value, "zh-tw")) {
    docsLanguages.value["zh-tw"] = docsLanguages.value["zh-hk"] ?? null;
  }
  dialogRef.value = true;
};

const hide = () => {
  fileList.value = {};
  docsLanguages.value = {};
  uploadedLinks.value = [];
  unmatchedFiles.value = [];
  dialogRef.value = false;
};

const removeFile = (key: any) => {
  delete fileList.value[key];
  nextTick(() => {
    const inputRef = uploadRefs.value[key];
    if (inputRef) {
      inputRef.value = "";
    }
  });
};

const submit = async () => {
  isLoading.value = true;

  try {
    const formData = new FormData();
    for (const [language, file] of Object.entries(fileList.value)) {
      formData.append("files[]", file, file.name);
      formData.append("languages[]", language);
    }
    formData.append("operator_info", JSON.stringify(operatorInfo.value));
    const res = await DocsServices.uploadDocuments(id.value, formData);
    uploadedLinks.value = res;
    ElMessage.success("Documents uploaded successfully");
    emits("submitted");
  } catch (error) {
    ElMessage.error("Upload file failed, file too large or invalid format");
  }
  isLoading.value = false;
};

__expose({
  show,
});

return (_ctx: any,_cache: any) => {
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: dialogRef.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dialogRef).value = $event)),
    title: _ctx.$t('fields.uploadDocumentsFor') + ' : ' + title.value,
    width: "900",
    "align-center": "",
    "before-close": hide
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", null, [
          _createElementVNode("input", {
            ref_key: "bulkInputRef",
            ref: bulkInputRef,
            type: "file",
            multiple: "",
            class: "d-none",
            onChange: handleBulkUpload
          }, null, 544),
          _createVNode(_component_el_button, {
            type: "success",
            onClick: triggerBulkUpload,
            disabled: isLoading.value,
            icon: _unref(Upload),
            plain: ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("action.bulkUpload")), 1)
            ]),
            _: 1
          }, 8, ["disabled", "icon"])
        ]),
        _createElementVNode("div", null, [
          _createVNode(_component_el_button, {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (dialogRef.value = false)),
            disabled: isLoading.value
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("action.cancel")), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: submit,
            loading: isLoading.value,
            plain: ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("action.upload")), 1)
            ]),
            _: 1
          }, 8, ["loading"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      (unmatchedFiles.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_el_alert, {
              type: "warning",
              closable: false,
              "show-icon": ""
            }, {
              title: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("fields.bulkUnmatched")), 1)
              ]),
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unmatchedFiles.value, (name, i) => {
                  return (_openBlock(), _createElementBlock("div", { key: i }, _toDisplayString(name), 1))
                }), 128))
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(docsLanguages.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", { key: index }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_el_tag, {
              for: 'file-input-' + index,
              class: "w-75px",
              type: "warning"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(index), 1)
              ]),
              _: 2
            }, 1032, ["for"]),
            _createElementVNode("input", {
              id: 'file-input-' + index,
              type: "file",
              ref_for: true,
              ref: (el) => (uploadRefs.value[index] = el),
              onChange: ($event: any) => (handleFileUpload($event, index)),
              disabled: isLoading.value,
              class: "form-control"
            }, null, 40, _hoisted_3),
            (fileList.value[index])
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  onClick: ($event: any) => (removeFile(index)),
                  disabled: isLoading.value,
                  type: "danger",
                  icon: _unref(Delete),
                  circle: ""
                }, null, 8, ["onClick", "disabled", "icon"]))
              : _createCommentVNode("", true)
          ])
        ]))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uploadedLinks.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", { key: index }, [
          _createVNode(_component_el_link, {
            href: item,
            target: "_blank",
            type: "success",
            class: "ms-3",
            underline: ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(item), 1)
            ]),
            _: 2
          }, 1032, ["href"])
        ]))
      }), 128))
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})